/**
 * FormPageLayout template - standard layout for form pages
 * Provides consistent structure for create/edit forms
 */

import { HStack, VStack } from '@chakra-ui/react';
import React from 'react';
import { Button } from '../../atoms/buttons/Button';
import { Card } from '../../atoms/layouts/Card';
import { BreadcrumbProps } from '../../molecules/navigation/Breadcrumb';
import { BaseComponentProps } from '../../types';
import { AdminLayout } from './AdminLayout';

export interface FormPageLayoutProps extends BaseComponentProps {
  /** Page title */
  title: string;
  /** Page subtitle */
  subtitle?: string;
  /** Breadcrumb navigation */
  breadcrumb?: BreadcrumbProps;
  /** Form content */
  children: React.ReactNode;
  /** Form submission handler */
  onSubmit?: () => void;
  /** Form cancellation handler */
  onCancel?: () => void;
  /** Submit button text */
  submitText?: string;
  /** Cancel button text */
  cancelText?: string;
  /** Whether form is in loading state */
  loading?: boolean;
  /** Whether submit button should be disabled */
  submitDisabled?: boolean;
  /** Additional form actions */
  formActions?: React.ReactNode;
  /** Custom actions to replace default buttons */
  actions?: React.ReactNode;
  /** Maximum width of the form */
  maxWidth?: string;
}

export const FormPageLayout: React.FC<FormPageLayoutProps> = ({
  title,
  subtitle,
  breadcrumb,
  children,
  onSubmit,
  onCancel,
  submitText = 'Save',
  cancelText = 'Cancel',
  loading = false,
  submitDisabled = false,
  formActions,
  actions,
  maxWidth,
  className,
  testId,
}) => {
  return (
    <AdminLayout
      header={{
        title,
        subtitle,
        breadcrumb,
      }}
      maxWidth={maxWidth || '800px'}
      className={className}
      data-testid={testId}
    >
      <Card padding={8}>
        <VStack spacing={8} align="stretch">
          {children}

          {actions ? (
            actions
          ) : (
            <HStack
              justify="space-between"
              pt={6}
              borderTop="1px solid"
              borderColor="gray.200"
            >
              <div>{formActions}</div>

              <HStack spacing={4}>
                {onCancel && (
                  <Button
                    variant="secondary"
                    onClick={onCancel}
                    isDisabled={loading}
                  >
                    {cancelText}
                  </Button>
                )}

                {onSubmit && (
                  <Button
                    variant="primary"
                    onClick={onSubmit}
                    loading={loading}
                    isDisabled={submitDisabled}
                  >
                    {submitText}
                  </Button>
                )}
              </HStack>
            </HStack>
          )}
        </VStack>
      </Card>
    </AdminLayout>
  );
};
